import React, { ReactNode } from 'react'
import classNames from 'classnames'

// evertel
import { Badge, Card, CardBody, Icon, Text, Row, FloatingMenu } from '@evertel/web/ui'
import { APIDataDocumentSchema } from '@evertel/types'
import { DocumentIcon } from './DocumentIcon'

interface DocumentTemplateCardProps {
    schema: APIDataDocumentSchema
    // navigates/acts on click; shows the "Use this template" arrow-cta when provided
    onClick?: () => void
    // shows a kebab with the provided menu (e.g. Edit/Clone/Disable) when provided
    menuItems?: ReactNode
    // mutes the card and replaces the arrow-cta with a "Disabled" label
    disabled?: boolean
    // shorter tile: drops the description, the card min-height and the gap above the cta
    compact?: boolean
    className?: string
    // set by a roving-tabindex parent (TemplateGrid): makes the card focusable even without
    // an onClick so a disabled template's kebab stays reachable, and the kebab shares the
    // value so only the active card's kebab is a Tab stop
    tabIndex?: number
    onFocus?: (e: React.FocusEvent<HTMLElement>) => void
}

const DocumentTemplateCard: React.FC<DocumentTemplateCardProps> = ({
    schema,
    onClick,
    menuItems,
    disabled = false,
    compact = false,
    className,
    tabIndex,
    onFocus
}) => {

    if (!schema) return null

    const isClickable = !!onClick
    const isFocusable = isClickable || tabIndex !== undefined

    const onKeyDown = (e: React.KeyboardEvent) => {
        // only act when the card itself is focused; Enter/Space on the kebab (or anything
        // inside the menu) must keep its own behavior instead of navigating the card
        if (e.target !== e.currentTarget) return

        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            onClick()
        }
    }

    return (
        <Card
            role={isFocusable ? 'button' : undefined}
            tabIndex={tabIndex ?? (isClickable ? 0 : undefined)}
            // a focusable card with nothing to click is a disabled template in a managed
            // list: announce it as such rather than as an inert button
            aria-disabled={(isFocusable && !isClickable) || undefined}
            aria-label={`${schema.name} template`}
            className={classNames(
                'document-template-card',
                {
                    'document-template-card-disabled': disabled,
                    'document-template-card-compact': compact
                },
                className
            )}
            onClick={onClick}
            onKeyDown={isFocusable ? onKeyDown : undefined}
            onFocus={onFocus}
        >
            <CardBody className="d-flex flex-column">
                <Row
                    align="between"
                    valign="center"
                >
                    <DocumentIcon
                        schema={schema}
                        width={32}
                        height={32}
                    />
                    <Badge
                        variant="soft"
                        style={{
                            // soft recipe from _badge.scss with the template's own color: full-color text on a ~12% tint
                            backgroundColor: `${schema.color}1F`,
                            color: 'currentColor'
                        }}
                    >
                        TEMPLATE
                    </Badge>
                </Row>
                <Row
                    valign="center"
                    className="mt-3"
                >
                    <Text
                        tag="div"
                        bold
                        className="template-title flex-grow-1">
                        {schema.name}
                    </Text>
                    {/* compact: the arrow cta sits beside the title instead of in its own footer row */}
                    {(compact && !disabled && !!onClick) && (
                        <Icon
                            name="arrow-right"
                            color="primary"
                            size={18}
                            className="ms-2 flex-shrink-0"
                        />
                    )}
                </Row>
                {!compact && (
                    <Text
                        tag="div"
                        size={13}
                        color="muted"
                        className="template-description mt-1">
                        {schema.description}
                    </Text>
                )}
                {/* kebab always anchors bottom-left; the right slot (cta / disabled label) is pushed
                    over with ms-auto so the layout doesn't shift between card variants.
                    compact cards only need this row for the kebab/disabled label */}
                {(!compact || !!menuItems || disabled) && (
                    <Row
                        valign="center"
                        className={compact ? 'mt-auto pt-1' : 'mt-auto pt-3'}>
                        {!!menuItems && (
                            <span onClick={(e) => e.stopPropagation()}>
                                <FloatingMenu
                                    label={`${schema.name} Actions Menu`}
                                    tabIndex={tabIndex}
                                    renderLabel={() => <Icon name="ellipsis-v" />}
                                    className="btn btn-sm btn-neutral rounded-2 d-flex align-items-center justify-content-center"
                                    style={{ width: 28, height: 28, padding: 0, flexShrink: 0 }}>
                                    {menuItems}
                                </FloatingMenu>
                            </span>
                        )}
                        {disabled ? (
                            <Row
                                valign="center"
                                className="ms-auto">
                                <Icon
                                    name="eye-slash"
                                    color="secondary-color"
                                    size={14}
                                    className="me-1"
                                />
                                <Text
                                    color="muted"
                                    size={12}
                                    bold
                                    italic>
                                    Disabled
                                </Text>
                            </Row>
                        ): (
                            (!compact && !!onClick) &&
                            <Row
                                valign="center"
                                className="ms-auto">
                                <Text
                                    color="primary"
                                    size={12}
                                    bold
                                    className="use-template-label me-2 fs-6">
                                    Use template
                                </Text>
                                <Icon
                                    name="arrow-right"
                                    color="primary"
                                    size={18}
                                />
                            </Row>
                        )}
                    </Row>
                )}
            </CardBody>
        </Card>
    )
}

export { DocumentTemplateCard }
